import React, { Component } from 'react'
import { Form, Input, Switch, Button, Space, Card, Divider, Collapse, Typography } from 'antd'
import { CaretRightOutlined, MinusCircleOutlined, PlusCircleOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons'
import { IDynamicBaseStruct, DynamicFieldStruct } from '../../interfaces/IDynamicFields'

type DynamicFieldsProps = {
  title?: string,
  name: string[],
  baseStruct: IDynamicBaseStruct,
  addLabel?: string,
  addFullWidth?: boolean,

  form?: any,

  collapseHeaderOptions?: [{
    [key: string]: string,
    value: string
  }]
}

export default class DynamicFields extends Component<DynamicFieldsProps> {

  private addDispatches: Array<any> = []

  private getFieldName = (name: string[]) => {
    if (name instanceof Array) {
      return name[name.length - 1]
    } else {
      return name
    }
  }

  private renderInput = (field: DynamicFieldStruct, itemKey?: number, parentKey?: number) => {
    const { form } = this.props

    if (field.type === 'select' && field.render) {
      return field.render(itemKey, parentKey, form)
    }
    if (field.type === 'boolean') {
      return <Switch />
    }
    if (field.type === 'number') {
      return <Input type="number" placeholder={field.label} />
    }
    if (field.type === 'custom') {
      return field.render(itemKey, parentKey, form)
    }

    return <Input placeholder={field.label} />
  }

  private renderSingleField = (formField: any, fieldName: string, fields: Record<any, DynamicFieldStruct>, parentKey?: number) => {
    return Object.keys(fields).map((key, index) => {
      const field = fields[key]

      if (field.type === 'object' && field.fields) {
        const dispatchKey = [formField.name, key].join('.')

        const add = () => {
          if (!this.addDispatches[dispatchKey]) return

          return this.addDispatches[dispatchKey]()
        }

        return (
          <Card
            title={field.label}
            bordered={true}
            key={`dynamic-card-${key}-${index}`}
            extra={<Button type="dashed" shape="circle" icon={<PlusCircleOutlined />} onClick={() => add()} />}
            style={{
              minHeight: 300,
              minWidth: 400,
              marginBottom: 20
            }}
          >
            {this.createList([formField.name, key], field.fields, false, field.allowReorder, true, index, key)}
          </Card>
        )
      } else {
        return (
          <div
            key={`dynamic-field-${fieldName}-${index}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '25vw'
            }}
          >
            <Form.Item
              {...formField}
              label={field.label}
              name={[formField.name, key]}
              style={{ width: '100%', marginRight: 10 }}
              valuePropName={field.type === 'boolean' ? 'checked' : undefined}
            >
              {this.renderInput(field, formField.name, parentKey)}
            </Form.Item>
          </div>
        )
      }
    })
  }

  private renderFields = (formName: string[], fieldName: string, fields: any, structFields: Record<any, DynamicFieldStruct>, removeDispatch: any, moveDispatch: any, collapsed = false, allowReorder = false, horizontal = false, recursiveIndex = 0) => {
    const { form, collapseHeaderOptions } = this.props

    if (horizontal) {
      return fields.map((field, fieldIdx) => {
        return (
          <Space
            size="large"
            align={horizontal ? 'center' : 'start'}
            direction={horizontal ? 'horizontal' : 'vertical'}
            key={`dynamic-list-${formName[formName.length - 1]}-${recursiveIndex}-field-${field.key}`}
            style={{ display: 'flex', flexWrap: 'wrap', marginBottom: 20, borderBottom: "1px solid #ccc" }}
          >
            {this.renderSingleField(field, fieldName, structFields, fieldIdx)}

            {
              horizontal ? (
                <Button type="dashed" shape="circle" title="Remove" icon={<MinusCircleOutlined />} onClick={() => removeDispatch(field.name)} />
              ) : null
            }

            {
              !allowReorder
                ? null
                : (
                  <Space size="small">
                    <Button type="ghost" shape="circle" title="Move up" disabled={fieldIdx === 0} icon={<ArrowUpOutlined />} onClick={() => moveDispatch(fieldIdx, fieldIdx - 1)} />
                    <Button type="ghost" shape="circle" title="Move down" disabled={fieldIdx === fields.length - 1} icon={<ArrowDownOutlined />} onClick={() => moveDispatch(fieldIdx, fieldIdx + 1)} />
                  </Space>
                )
            }
          </Space>
        )
      })
    } else {
      return fields.map((field, fieldIdx) => {
        const renderFields = (
          <div
            key={`dynamic-card-${formName[formName.length - 1]}-${field.key}`}
          >
            <Card
              bordered={false}
              extra={<Button type="dashed" shape="circle" icon={<MinusCircleOutlined />} onClick={() => removeDispatch(field.name)} />}
            >
              <Space
                size="large"
                align={horizontal ? 'center' : 'start'}
                direction={horizontal ? 'horizontal' : 'vertical'}
                style={{ display: 'flex', marginBottom: 20 }}
              >
                {this.renderSingleField(field, fieldName, structFields, fieldIdx)}

                {
                  horizontal ? (
                    <Button type="dashed" shape="circle" icon={<MinusCircleOutlined />} onClick={() => removeDispatch(field.name)} />
                  ) : null
                }

                {
                  !allowReorder
                    ? null
                    : (
                      <Space size="small">
                        <Button type="ghost" shape="circle" title="Move up" disabled={fieldIdx === 0} icon={<ArrowUpOutlined />} onClick={() => moveDispatch(fieldIdx, fieldIdx - 1)} />
                        <Button type="ghost" shape="circle" title="Move down" disabled={fieldIdx === fields.length - 1} icon={<ArrowDownOutlined />} onClick={() => moveDispatch(fieldIdx, fieldIdx + 1)} />
                      </Space>
                    )
                }
              </Space>
            </Card>

            {
              fields.length > 0 ? <Divider /> : null
            }
          </div>
        )

        if (collapsed) {
          const collapseKey = [...formName, field.name].join('.')
          let header = 'Collapsed'

          if (form) {
            const fieldValue = form.getFieldValue([...formName, field.name])
            if (fieldValue) {
              if (collapseHeaderOptions) {
                Object.keys(fieldValue).forEach(key => {
                  collapseHeaderOptions.filter(option => Object.keys(option).includes(key)).forEach(option => {
                    if (option[key] === fieldValue[key]) {
                      header = option.value || fieldValue[key]
                    }
                  })
                })
              } else {
                // Get the first field value as collapse header
                const firstKey = Object.keys(fieldValue).length > 0 ? Object.keys(fieldValue)[0] : null
                if (firstKey && fieldValue[firstKey]) {
                  header = fieldValue[firstKey]
                }
              }
            }
          }

          return (
            <Collapse.Panel header={typeof header === 'string' ? header : 'Collapsed'} key={collapseKey}>
              {renderFields}
            </Collapse.Panel>
          )
        }

        return renderFields
      })
    }
  }

  private createList = (formName: string[], structFields: Record<any, DynamicFieldStruct>, collapsed = false, allowReorder = false, horizontal = false, recursiveIndex = 0, fieldKey: string | null = null) => {
    const { title, addLabel, addFullWidth } = this.props
    const fieldName = this.getFieldName(formName)

    return (
      <Form.List
        name={formName}
        key={`dynamic-list-${formName[formName.length - 1]}-${recursiveIndex}`}
      >
        {(fields, { add, remove, move }) => {
          if (fieldKey) {
            const dispatchKey = [...formName]
            this.addDispatches[dispatchKey.join('.')] = add
          }

          return (
            <>
              {
                !horizontal && title && fields.length > 0 ? (
                  <Typography.Title level={5}>{title}</Typography.Title>
                ) : null
              }

              {
                collapsed ? (
                  <Collapse
                    bordered={false}
                    expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                  >
                    {this.renderFields(formName, fieldName, fields, structFields, remove, move, collapsed, allowReorder, horizontal, recursiveIndex)}
                  </Collapse>
                ) : this.renderFields(formName, fieldName, fields, structFields, remove, move, collapsed, allowReorder, horizontal, recursiveIndex)
              }

              {
                !horizontal ? (
                  <Button type="dashed"
                    block={addFullWidth || false}
                    onClick={() => add()}
                    style={{
                      marginTop: 20
                    }}
                  >
                    {addLabel || 'Add'}
                  </Button>
                ) : null
              }
            </>
          )
        }}
      </Form.List>
    )
  }

  render() {
    const { name, baseStruct } = this.props
    const fieldName = this.getFieldName(name)

    // Check if value exists
    if (!Object.keys(baseStruct).includes(fieldName)) {
      return null
    }

    return this.createList(name, baseStruct[fieldName].fields, baseStruct[fieldName].collapsed)
  }
}
